import { toast } from 'sonner';

/**
 * Handle common API response errors
 */
export function handleApiError(response: Response, data?: any): never {
  switch (response.status) {
    case 401:
      // Unauthorized - redirect to login
      window.location.href = '/auth/login';
      throw new Error('Authentication required');

    case 403:
      // Forbidden - redirect to projects with error message
      toast.error('You don\'t have permission to access that resource');
      window.location.href = '/projects';
      throw new Error('Access forbidden');

    case 404:
      throw new Error(data?.error || 'Resource not found');

    case 422:
      // Validation error
      throw new Error(data?.error || 'Invalid data provided');

    case 500:
      toast.error('Server error occurred. Please try again.');
      throw new Error('Internal server error');

    default:
      throw new Error(data?.error || `Request failed with status ${response.status}`);
  }
}

/**
 * Make an authenticated API request with error handling
 */
export async function apiRequest<T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    handleApiError(response, data);
  }

  return data;
}

/**
 * Make an API request that returns a Response object (for file downloads, etc.)
 */
export async function apiRequestRaw(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
  });

  if (response.status === 403) {
    toast.error('You don\'t have permission to access that resource');
    window.location.href = '/projects';
    throw new Error('Access forbidden');
  }

  if (response.status === 401) {
    window.location.href = '/auth/login';
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response;
}
